import * as XLSX from "xlsx";

export function assertWorkbookDimensions(workbook) {
  // Bound work before any full rectangular conversion, including manual fallback.
  for (const name of workbook.SheetNames) {
    const range = XLSX.utils.decode_range(workbook.Sheets[name]["!ref"] || "A1");
    const rows = range.e.r + 1, columns = range.e.c + 1;
    if (rows > 50001 || columns > 10001 || rows * columns > 2000000)
      throw new Error("Worksheet dimensions are too large. Remove unused rows and columns before uploading.");
  }
}

// Excel dates are calendar values, never UTC instants. Preserve their serial date parts.
export function readExcelWorkbook(bytes) {
  const workbook = XLSX.read(bytes, { type: "array", cellDates: false, cellNF: true, raw: true });
  assertWorkbookDimensions(workbook);
  for (const name of workbook.SheetNames) {
    const sheet = workbook.Sheets[name];
    for (const key of Object.keys(sheet)) {
      const cell = sheet[key];
      if (key.startsWith("!") || cell.t !== "n" || !cell.z || !XLSX.SSF.is_date(cell.z)) continue;
      const parts = XLSX.SSF.parse_date_code(cell.v, { date1904: !!workbook.Workbook?.WBProps?.date1904 });
      if (parts) {
        cell.t = "s";
        cell.v = `${parts.y}-${String(parts.m).padStart(2, "0")}-${String(parts.d).padStart(2, "0")}`;
        delete cell.w;
      }
    }
  }
  return workbook;
}

export function excelStructureSamples(workbook) {
  assertWorkbookDimensions(workbook);
  if (workbook.SheetNames.length > 20) throw new Error("AI structure detection supports up to 20 worksheets. Select a smaller workbook.");
  return { sheets: workbook.SheetNames.map(name => {
    const sheet = workbook.Sheets[name];
    const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
    if (range.e.r > 50000 || range.e.c > 10000) throw new Error("Worksheet dimensions are too large. Remove unused rows and columns before uploading.");
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: true, range: { s: { r: 0, c: 0 }, e: { r: Math.min(range.e.r, 99), c: Math.min(range.e.c, 39) } } });
    // Expand merged labels only in the sample. Original data is never rewritten by AI.
    for (const merge of sheet["!merges"] || []) {
      const value = rows[merge.s.r]?.[merge.s.c];
      if (!value) continue;
      for (let r = merge.s.r; r <= Math.min(merge.e.r, 99); r++)
        for (let c = merge.s.c; c <= Math.min(merge.e.c, 39); c++) {
          rows[r] ||= [];
          if (!rows[r][c]) rows[r][c] = value;
        }
    }
    return { name, rowCount: range.e.r + 1, columnCount: range.e.c + 1,
      rows: rows.map(row => row.map(value => String(value ?? "").slice(0, 300))) };
  }) };
}

export function normalizeExcelStructure(workbook, structure) {
  assertWorkbookDimensions(workbook);
  const { sheet: name, orientation, headerStart } = structure;
  let { headerEnd } = structure;
  let { firstColumn, lastColumn } = structure;
  const source = workbook.Sheets[name];
  if (!source || !["rows", "columns"].includes(orientation) ||
      ![headerStart, headerEnd, firstColumn, lastColumn].every(Number.isInteger) ||
      headerStart < 1 || headerEnd < headerStart || headerEnd - headerStart > 9 ||
      firstColumn < 1 || lastColumn < firstColumn || lastColumn - firstColumn >= 200)
    throw new Error("The detected Excel structure is invalid. Review the original worksheet.");
  const range = XLSX.utils.decode_range(source["!ref"] || "A1");
  if (range.e.r > 50000 || range.e.c > 10000) throw new Error("Worksheet dimensions are too large.");
  let matrix = XLSX.utils.sheet_to_json(source, { header: 1, defval: "", raw: true, range: 0 });
  if (orientation === "columns") matrix = Array.from({ length: range.e.c + 1 }, (_, col) => matrix.map(row => row[col] ?? ""));
  if (headerEnd >= matrix.length || lastColumn > (orientation === "rows" ? range.e.c + 1 : range.e.r + 1))
    throw new Error("The detected Excel structure is outside the worksheet.");
  // Calendar values below the first heading row are record data, not a second heading.
  // Keep those records even when the AI overestimates a multi-row heading boundary.
  for (let r = headerStart; r < headerEnd; r++) {
    if (matrix[r].slice(firstColumn - 1, lastColumn).some(value =>
      /^\d{4}-\d{2}-\d{2}(?:$|T| )/.test(String(value)) || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(String(value)))) {
      headerEnd = r;
      break;
    }
  }
  // Fill merged heading groups only; never fill merged engagement values.
  const headings = matrix.slice(headerStart - 1, headerEnd).map(row => [...row]);
  for (const merge of source["!merges"] || []) {
    const sr = orientation === "rows" ? merge.s.r : merge.s.c;
    const er = orientation === "rows" ? merge.e.r : merge.e.c;
    const sc = orientation === "rows" ? merge.s.c : merge.s.r;
    const ec = orientation === "rows" ? merge.e.c : merge.e.r;
    if (sr < headerStart - 1 || er >= headerEnd) continue;
    const value = matrix[sr]?.[sc];
    for (let r = sr; r <= er; r++) for (let c = sc; c <= ec; c++)
      if (!headings[r - headerStart + 1][c]) headings[r - headerStart + 1][c] = value;
  }
  // Recover adjacent omitted headings, but never cross a blank separator into notes/other tables.
  const hasHeading = index => headings.some(row => String(row[index] ?? "").trim());
  while (firstColumn > 1 && hasHeading(firstColumn - 2)) firstColumn--;
  while (hasHeading(lastColumn)) lastColumn++;
  if (lastColumn - firstColumn >= 200) throw new Error("The detected table exceeds 200 columns.");
  const headers = Array.from({ length: lastColumn - firstColumn + 1 }, (_, index) =>
    [...new Set(headings.map(row => String(row[firstColumn - 1 + index] ?? "").trim()).filter(Boolean))].join(" "));
  const data = matrix.slice(headerEnd);
  // Drop only columns empty in every heading and data row. Missing names on populated columns remain errors.
  const keptColumns = headers.map((header, index) => ({header, index})).filter(({header, index}) =>
    header || data.some(row => String(row[firstColumn - 1 + index] ?? "").trim()));
  const rows = [keptColumns.map(column => column.header), ...data.map(row => keptColumns.map(({index}) => row[firstColumn - 1 + index] ?? ""))];
  const normalized = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(normalized, XLSX.utils.aoa_to_sheet(rows), name);
  normalized.Workbook = { WBProps: { date1904: !!workbook.Workbook?.WBProps?.date1904 } };
  normalized.piersStructure = { ...structure, headerEnd,
    headingRows: matrix.slice(headerStart - 1, headerEnd).map(row => row.slice(firstColumn - 1, lastColumn))
  };
  return normalized;
}
